/**
 * FeedbackButton — Feedback submission for pilot issue reporting.
 * Sprint 18, Deliverable 5 — Issue tracking during pilot.
 *
 * Supports two variants:
 * - "floating" (default): Fixed position button at bottom-right
 * - "sidebar": Inline sidebar-style link that opens the feedback dialog
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiFetch } from '../lib/api';
import { MessageSquare } from 'lucide-react';

type FeedbackType = 'bug' | 'suggestion' | 'question';

const FEEDBACK_TYPES: { value: FeedbackType; label: string }[] = [
  { value: 'bug', label: 'Bug Report' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'question', label: 'Question' },
];

interface FeedbackButtonProps {
  variant?: 'floating' | 'sidebar';
}

export default function FeedbackButton({ variant = 'floating' }: FeedbackButtonProps) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    setError(null);

    try {
      await apiFetch('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({
          type: feedbackType,
          message: message.trim(),
          page: window.location.pathname,
          user_agent: navigator.userAgent,
        }),
      });
      setSubmitted(true);
      setMessage('');
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
      }, 2000);
    } catch {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [feedbackType, message]);

  if (!user) return null;

  // Trigger button
  if (!isOpen) {
    if (variant === 'sidebar') {
      return (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2.5 w-full px-3 py-2 text-sm rounded-lg
                     transition-colors duration-150 cursor-pointer text-left
                     text-content-secondary hover:bg-surface-sunken hover:text-content-primary border-none bg-transparent"
        >
          <MessageSquare className="h-4 w-4 shrink-0" />
          Feedback
        </button>
      );
    }

    // floating variant — hidden when sidebar variant is being used elsewhere
    return null;
  }

  // Feedback dialog (shared between both variants)
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-[420px] bg-surface-raised border border-edge-default rounded-xl p-5 shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="m-0 text-base font-bold text-content-primary">Send Feedback</h3>
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              setError(null);
            }}
            className="bg-transparent border-none text-content-tertiary text-xl cursor-pointer"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {submitted ? (
          <div className="text-center py-6 text-success-500 text-sm">Thank you! Your feedback has been submitted.</div>
        ) : (
          <>
            {/* Type selector */}
            <div className="flex gap-2 mb-3.5">
              {FEEDBACK_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setFeedbackType(t.value)}
                  className={`flex-1 py-1.5 px-2 text-xs font-semibold rounded-md border cursor-pointer ${
                    feedbackType === t.value
                      ? 'border-action-primary bg-action-primary/15 text-action-primary'
                      : 'border-edge-default bg-transparent text-content-secondary'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Message */}
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe the issue or suggestion..."
              maxLength={2000}
              rows={4}
              className="w-full p-3 text-sm bg-surface-base text-content-primary border border-edge-default rounded-lg resize-y font-sans box-border"
            />

            {error && <div className="text-danger-400 text-[13px] mt-2">{error}</div>}

            <div className="flex justify-end gap-2 mt-3.5">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setError(null);
                }}
                className="py-2 px-4 text-[13px] bg-transparent text-content-secondary border border-edge-default rounded-md cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!message.trim() || submitting}
                className={`py-2 px-4 text-[13px] font-semibold border-none rounded-md ${
                  !message.trim() || submitting
                    ? 'bg-surface-sunken text-content-tertiary cursor-default'
                    : 'bg-action-primary text-surface-base cursor-pointer'
                }`}
              >
                {submitting ? 'Sending...' : 'Submit'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
